"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import SideTabbedButton from "@/components/generic/SideTabbedButton";
import theme, { blueGradientSxDown } from "@/theme/theme";

import arrow from "@/assets/images/icons/arrow.webp"

type Category = { _id: string; title: string };
type Author = { _id: string; name: string };
type ResolvedImage = { url: string; alt?: string } | null;

type ArticleRow = {
  _id: string;
  title: string;
  slug: string;
  description?: string;
  publishedAt?: string;
  mainImage: ResolvedImage;
  author: Author | null;
  categories: ReadonlyArray<Category>;
};

type ArticlesResponse =
  | {
      ok: true;
      total: number;
      items: ReadonlyArray<ArticleRow>;
      categories: ReadonlyArray<Category>;
      authors: ReadonlyArray<Author>;
    }
  | { ok: false; error: string };

type QueryState = {
  page: number;
  pageSize: number;
  q: string;
  categoryId: string[];
  author: string[];
};

const PAGE_SIZE_DEFAULT = 5;

function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "2-digit" });
}

function AuthorLabel(props: { name?: string; onClick?: (name: string) => void }) {
  if (!props.name) return null;
  return (
    <Typography
      component="span"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        props.onClick?.(props.name!)
      }}
      sx={{
        fontWeight: 600,
        cursor: "pointer",
        zIndex: 10,
        transition: "opacity 0.2s",
        "&:hover": { 
            textDecoration: "underline",
            opacity: 0.8 
        },
      }}
    >
      <Box
        component="span"
        sx={{
            ...blueGradientSxDown(theme),
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
        }}
      >
        {props.name}
      </Box>
    </Typography>
  );
}

function Tags(props: { categories: ReadonlyArray<Category>; onCategoryClick?: (id: string) => void }) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {props.categories.map((c) => (
        <Chip
          key={c._id}
          label={c.title}
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            props.onCategoryClick?.(c._id)
            }}
          sx={{
            borderColor: theme.palette.brand.border1.main,
            color: theme.palette.text.secondary,
            bgcolor: "transparent",
            borderRadius: 1.5,
            px: 0.5,
            cursor: "pointer",
            "&:hover": {
                bgcolor: "rgba(255,255,255,0.05)",
                borderColor: "white"
            }
          }}
        />
      ))}
    </Stack>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "transparent",
    borderRadius: 3,
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
    "&.Mui-focused fieldset": { borderColor: theme.palette.brand.border1.main },
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
} as const;

const autoCompleteSlotProps = {
  paper: {
    sx: {
      bgcolor: theme.palette.background.default,
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "none",
      borderRadius: 2,
      mt: 0.5,
    },
  },
  listbox: {
    sx: {
      "& .MuiAutocomplete-option": {
        "&[aria-selected='true']": {
          backgroundColor: theme.palette.brand.cardHover1.main,
        },
        "&.Mui-focused": {
          backgroundColor: theme.palette.brand.cardHover1.main,
        },
      },
    },
  },
} as const;

const cardSx = {
  borderRadius: 2,
  border: "1px solid rgba(255,255,255,0.12)",
  bgcolor: theme.palette.background.default,
  transition: "border-color 150ms ease",
  "&:hover": { borderColor: theme.palette.brand.border1.main },
} as const;

export default function ArticlesSectionClient(): ReactElement {
  const [categories, setCategories] = useState<ReadonlyArray<Category>>([]);
  const [allAuthors, setAllAuthors] = useState<ReadonlyArray<Author>>([]);
  const [items, setItems] = useState<ReadonlyArray<ArticleRow>>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<ReadonlyArray<string>>([]);
  const [selectedAuthors, setSelectedAuthors] = useState<ReadonlyArray<string>>([]);

  const [query, setQuery] = useState<QueryState>({
    page: 1,
    pageSize: PAGE_SIZE_DEFAULT,
    q: "",
    categoryId: [],
    author: [],
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / query.pageSize)), [total, query.pageSize]);

  const authorOptions = useMemo<ReadonlyArray<string>>(() => {
    return allAuthors.map(a => a.name).sort((a, b) => a.localeCompare(b));
}, [allAuthors]);

  const fetchArticles = useCallback(async (qState: QueryState) => {
    setLoading(true);
    setErrorText("");
    try {
      const offset = (qState.page - 1) * qState.pageSize;
      const body = {
        offset,
        limit: qState.pageSize,
        q: qState.q.trim().length ? `*${qState.q.trim()}*` : undefined,
        categoryId: qState.categoryId.length > 0 ? qState.categoryId : undefined,
        author: qState.author.length > 0 ? qState.author : undefined,
      };

      const res = await fetch("/api/articles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        cache: "no-store",
      });

      const data = (await res.json()) as ArticlesResponse;
      if (!res.ok || !data.ok) throw new Error((!data.ok && data.error) || "Request failed");

      setItems(data.items);
      setTotal(data.total);
      setCategories(data.categories);
      setAllAuthors(data.authors);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setErrorText(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchArticles(query);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (items.length === 0 && total === 0 && categories.length === 0) return;
    void fetchArticles(query);
  }, [query, fetchArticles]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPage = (p: number) => setQuery((prev) => ({ ...prev, page: Math.min(Math.max(1, p), totalPages) }));

  const onSearch = () => setPage(1);

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      categoryId: selectedCategoryIds as string[],
      author: selectedAuthors as string[],
      page: 1,
    }));
  }, [selectedCategoryIds, selectedAuthors]);

  return (
    <Grid container spacing={4} sx={{ width: {xs: "90%", md: "75%"} }}>
      <Grid size={{ xs: 12, md: 3 }}>
        <Stack spacing={2.25}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Search
          </Typography>

          <TextField
            fullWidth
            size="medium"
            placeholder="Article name"
            value={query.q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery((p) => ({ ...p, q: e.target.value }))}
            sx={inputSx}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={categories}
            getOptionLabel={(o: Category) => o.title}
            value={categories.filter((c) => selectedCategoryIds.includes(c._id))}
            onChange={(_, newValue: ReadonlyArray<Category>) =>
              setSelectedCategoryIds(newValue.map((v) => v._id))
            }
            renderTags={(value: ReadonlyArray<Category>, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option._id}
                  label={option.title}
                  size="small"
                  sx={{ borderRadius: 1.5 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Categories"
                size="medium"
                sx={inputSx}
              />
            )}
            slotProps={autoCompleteSlotProps}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={authorOptions}
            value={selectedAuthors as string[]}
            onChange={(_, newValue: ReadonlyArray<string>) => setSelectedAuthors(newValue)}
            renderTags={(value: ReadonlyArray<string>, getTagProps) =>
              value.map((name, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={name}
                  label={name}
                  size="small"
                  sx={{ borderRadius: 1.5 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Authors"
                size="medium"
                sx={inputSx}
              />
            )}
            slotProps={autoCompleteSlotProps}
            filterSelectedOptions
          />
          <Stack width="100%" direction="row" alignItems="center" justifyContent="center" position="relative" sx={{
                    zIndex: 2
                }}>
                    <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} action={() => {
                        onSearch();
                    }}>
                        <div className="flex items-center justify-center gap-1">
                            <Typography component="span" variant="h6" marginRight={1}>
                                Search
                            </Typography>
                            <span className="arrow-clip">
                                <span className="arrow-slide">
                                    <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                </span>
                            </span>
                        </div>
                    </SideTabbedButton>
                </Stack>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 9 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Tabs
            value={
              selectedCategoryIds.length === 1 && selectedAuthors.length === 0 
                ? selectedCategoryIds[0] 
                : "ALL"
            }
            onChange={(_, val: string) => {
              if (val === "ALL") {
                setSelectedCategoryIds([]);
              } else {
                setSelectedCategoryIds([val]);
              }
              setSelectedAuthors([]);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="inherit"
            sx={{
              "& .MuiTabs-scrollButtons": { color: "text.secondary" },
              "& .MuiTabs-indicator": {
              ...(blueGradientSxDown(theme) as object),
              WebkitBackgroundClip: "initial",
              WebkitTextFillColor: "initial",
              height: "3px",
              },
            }}
          >
            <Tab value="ALL" label="All Articles" sx={{ textTransform: "none", minHeight: 36 }} />
            {categories.map((c) => (
              <Tab
                key={c._id}
                value={c._id}
                label={c.title}
                sx={{ textTransform: "none", minHeight: 36, px: 1 }}
              />
            ))}
          </Tabs>
        </Stack>

        <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.12)" }} />

        <Stack spacing={2.5}>
          {items.map((post) => (
            <Card key={post._id} elevation={0} sx={cardSx}>
              <CardActionArea component={Link} href={`/article/${encodeURIComponent(post.slug)}`} sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Grid container spacing={2} alignItems="stretch">
                  <Grid size={{ xs: 12, sm: 4, md: 3.5 }}>
                    {post.mainImage?.url && (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          aspectRatio: "1 / 1",
                          borderRadius: 1.5,
                          overflow: "hidden",
                        }}
                      >
                        <Image
                          src={post.mainImage.url}
                          alt={post.mainImage.alt ?? post.title}
                          fill
                          style={{ objectFit: "cover" }}
                          sizes="(min-width: 1200px) 260px, (min-width: 900px) 33vw, 100vw"
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, sm: 8, md: 8.5 }}>
                    <CardContent sx={{ py: 0 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <AuthorLabel name={post.author?.name ?? ""} onClick={(name) => { setSelectedAuthors([name]); setSelectedCategoryIds([]) }} />
                          {post.publishedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(post.publishedAt)}
                            </Typography>
                          )}
                        </Stack>

                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                          {post.title}
                        </Typography>

                        {post.description && (
                          <Typography variant="body2" color="text.secondary">
                            {post.description}
                          </Typography>
                        )}

                        <Tags categories={post.categories} onCategoryClick={(id) => { setSelectedCategoryIds([id]); setSelectedAuthors([]); }} />
                      </Stack>
                    </CardContent>
                  </Grid>
                </Grid>
              </CardActionArea>
            </Card>
          ))}

          {items.length === 0 && !loading && (
            <Typography color="text.secondary">No results.</Typography>
          )}
          {errorText && (
            <Typography color="error" variant="body2">
              {errorText}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          // useFlexGap so the spacing survives wrapping; Stack's default margin-based
          // spacing leaves a stray offset on wrapped rows.
          useFlexGap
          spacing={{ xs: 1, sm: 2 }}
          sx={{ mt: 4, color: "text.secondary" }}
        >
          <Typography
            component="button"
            onClick={() => setPage(query.page - 1)}
            disabled={query.page <= 1}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              color: "inherit",
              fontSize: "14px",
              cursor: query.page <= 1 ? "not-allowed" : "pointer",
            }}
          >
            previous page
          </Typography>

          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2 }}
            alignItems="center"
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
          >
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => i + 1).map((p) => (
              <Typography
                key={p}
                component="button"
                onClick={() => setPage(p)}
                sx={{
                  background: "none",
                  border: 0,
                  padding: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  fontWeight: p === query.page ? 700 : 400,
                  ...(p === query.page
                    ? {
                        ...blueGradientSxDown(theme),
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        color: "transparent", 
                      }
                    : {
                        color: "inherit",
                      }),
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                {p}
              </Typography>
            ))}
            {totalPages > 7 && <Typography>…</Typography>}
            {totalPages > 7 && (
              <Typography
                component="button"
                onClick={() => setPage(totalPages)}
                style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
              >
                {totalPages}
              </Typography>
            )}
          </Stack>

          <Typography
            component="button"
            onClick={() => setPage(query.page + 1)}
            disabled={query.page >= totalPages}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              color: "inherit",
              fontSize: "14px",
              cursor: query.page >= totalPages ? "not-allowed" : "pointer",
            }}
          >
            next page
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
}
